import { trpc } from '@tencent/tpro_rmonitor_user_manager_user_role_auth';
import { useRequest } from 'ahooks';
import { useMemo } from 'react';
import { getUserId } from '@/infrastructure/auth';
import { TRPCPost } from '@/infrastructure/axios';
import { API_PREFIX } from '@/infrastructure/axios/code';
import { getProductId } from '@/infrastructure/product-info';
import { PermNodeValue } from '@/presentation/components/permission-manage/config';
import { axiosRequestConfig } from '@/presentation/components/user-manage/api';
import { getAllRoles } from '@/presentation/pages/product/permission/api';
import { useStores } from '@/presentation/store';
import { userRoleService } from '@/service/user-role';
import { WithTypeCast } from '@raft/utils';
type IRole = WithTypeCast<trpc.rmonitor.user_manager.IRole>;

export function useRoleList(productId: string): {
  roles: IRole[]
} {
  const { data } = useRequest(getAllRoles, {
    cacheKey: `product-role-list-${productId}`,
  });

  return {
    roles: data || [],
  };
}

export const getPermissions = async (productId: string, userId: string) => {
  const resp = await TRPCPost(
    `/${API_PREFIX}/trpc/trpc.rmonitor.user_manager.UserRoleAuth/GetRoleByUser`,
    {
      productId,
      username: userId,
    },
    axiosRequestConfig(),
  );
  const roleId = resp?.role?.id;
  const permissions = await userRoleService.getRolePermission({
    roleId,
  });

  return {
    permissions,
    roleId,
    isAdmin: resp?.role?.name === '管理员',
  };
};

export function useBatchPermission(nameList: PermNodeValue[]): {
  permission: Map<PermNodeValue, boolean>
} {
  const { rawPerm, isSuperUser } = useStores().ProductStore;

  const permission = useMemo(() => {
    const perm = new Map<PermNodeValue, boolean>();
    nameList.forEach((name: PermNodeValue) => {
      const hasPerm = isSuperUser || rawPerm?.isAdmin || (!rawPerm?.permissions.includes(name) ?? true);
      perm.set(name, hasPerm);
    });

    return perm;
  }, []);

  return {
    permission,
  };
}

export function usePermission(name: PermNodeValue): {
  hasPermission: boolean;
} {
  const userId = getUserId();
  const productId = getProductId();
  const { data } = useRequest(() => getPermissions(productId, userId), {
    cacheKey: `${userId}-${productId}`,
  });

  const hasPermission = useMemo(() => data?.isAdmin || (!data?.permissions.includes(name) ?? true), [data]);
  return {
    hasPermission,
  };
}

export function useRolePermissions() {
  const userId = getUserId();
  const productId = getProductId();

  const { data } = useRequest(() => getPermissions(productId, userId), {
    cacheKey: `${userId}-${productId}`,
  });

  return {
    permissions: data?.permissions,
  };
}
